import { useSeoMeta, useHead } from '@unhead/react'
import { useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'

function ConcreteStepsCalculator() {
  useSeoMeta({
    title: 'Concrete Steps Calculator | Stair Volume Estimator',
    description: 'Calculate concrete for steps and stairs. Free concrete steps calculator with accurate volume and bag estimates. Enter step dimensions for instant results.',
    ogTitle: 'Concrete Steps Calculator | Concrete-Calculator.com',
    ogDescription: 'Calculate concrete volume for steps and stairs.',
    ogUrl: 'https://www.concrete-calculator.com/concrete-steps-calculator/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/concrete-steps-calculator/' }
    ]
  })

  const [stepWidth, setStepWidth] = useState('')
  const [stepDepth, setStepDepth] = useState('')
  const [stepHeight, setStepHeight] = useState('')
  const [numSteps, setNumSteps] = useState('')
  const [landingLength, setLandingLength] = useState('0')
  const [landingThickness, setLandingThickness] = useState('0')
  const [results, setResults] = useState<{
    stepsVolume: number
    landingVolume: number
    totalCubicFeet: number
    cubicYards: number
    bags80lb: number
  } | null>(null)

  const calculate = () => {
    const width = parseFloat(stepWidth)
    const depth = parseFloat(stepDepth)
    const height = parseFloat(stepHeight)
    const steps = parseFloat(numSteps)
    const landingLen = parseFloat(landingLength)
    const landingThick = parseFloat(landingThickness)

    if (isNaN(width) || isNaN(depth) || isNaN(height) || isNaN(steps) || 
        width <= 0 || depth <= 0 || height <= 0 || steps <= 0) {
      return
    }

    // Convert inches to feet
    const depthFeet = depth / 12
    const heightFeet = height / 12
    const landingThickFeet = landingThick / 12

    // Calculate steps volume using triangular prism formula
    // Each step forms a staircase shape - calculate as cumulative triangular volume
    let totalStepVolume = 0
    for (let i = 1; i <= steps; i++) {
      const stepVolume = width * depthFeet * (heightFeet * i)
      totalStepVolume += stepVolume
    }

    // Calculate landing if provided
    const landingVolume = (landingLen > 0 && landingThick > 0) 
      ? width * landingLen * landingThickFeet 
      : 0

    const totalVolume = totalStepVolume + landingVolume
    const cubicYards = totalVolume / 27
    const bags80 = Math.ceil(totalVolume / 0.6)

    setResults({
      stepsVolume: totalStepVolume,
      landingVolume: landingVolume,
      totalCubicFeet: totalVolume,
      cubicYards: cubicYards,
      bags80lb: bags80
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Concrete Steps Calculator Results',
    inputs: [
      { label: 'Step Width', value: `${stepWidth} feet` },
      { label: 'Tread Depth', value: `${stepDepth} inches` },
      { label: 'Rise Height', value: `${stepHeight} inches` },
      { label: 'Number of Steps', value: numSteps },
      { label: 'Landing Length', value: `${landingLength} feet` },
      { label: 'Landing Thickness', value: `${landingThickness} inches` }
    ],
    results: [
      { label: 'Steps Volume', value: `${results?.stepsVolume.toFixed(2)} ft³` },
      { label: 'Landing Volume', value: `${results?.landingVolume.toFixed(2)} ft³` },
      { label: 'Total Volume', value: `${results?.totalCubicFeet.toFixed(2)} ft³` },
      { label: 'Cubic Yards', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: '80 lb Bags Needed', value: `${results?.bags80lb} bags` }
    ]
  })

  const reset = () => {
    setStepWidth('')
    setStepDepth('')
    setStepHeight('')
    setNumSteps('')
    setLandingLength('0')
    setLandingThickness('0')
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Concrete Steps Calculator
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
        </nav>

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Calculate Concrete for Steps</h2>
          
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">Step Dimensions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="stepWidth" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Step Width (feet)
                </label>
                <input
                  id="stepWidth"
                  type="number"
                  value={stepWidth}
                  onChange={(e) => setStepWidth(e.target.value)}
                  placeholder="e.g., 4"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label htmlFor="stepDepth" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Tread Depth (inches)
                </label>
                <input
                  id="stepDepth"
                  type="number"
                  value={stepDepth}
                  onChange={(e) => setStepDepth(e.target.value)}
                  placeholder="e.g., 11"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.5"
                />
              </div>

              <div>
                <label htmlFor="stepHeight" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Rise Height (inches)
                </label>
                <input
                  id="stepHeight"
                  type="number"
                  value={stepHeight}
                  onChange={(e) => setStepHeight(e.target.value)}
                  placeholder="e.g., 7"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.5"
                />
              </div>

              <div>
                <label htmlFor="numSteps" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Number of Steps
                </label>
                <input
                  id="numSteps"
                  type="number"
                  value={numSteps}
                  onChange={(e) => setNumSteps(e.target.value)}
                  placeholder="e.g., 3"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="1"
                  step="1"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">Landing (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="landingLength" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Landing Length (feet)
                </label>
                <input
                  id="landingLength"
                  type="number"
                  value={landingLength}
                  onChange={(e) => setLandingLength(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label htmlFor="landingThickness" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                  Landing Thickness (inches)
                </label>
                <input
                  id="landingThickness"
                  type="number"
                  value={landingThickness}
                  onChange={(e) => setLandingThickness(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                  min="0"
                  step="0.5"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Results</h3>
                <ExportButtons data={getExportData()} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Steps Volume</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.stepsVolume.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Landing Volume</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.landingVolume.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Total Volume</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.totalCubicFeet.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Cubic Yards</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicYards.toFixed(2)} yd³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200 sm:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">80 lb Bags Needed</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.bags80lb} bags</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter step width in feet, tread depth and rise height in inches, and total number of steps. Optionally add landing dimensions if you have a landing platform.
            </p>
            <p>
              <strong>Standard Dimensions:</strong> Tread depth 10-11", rise height 7-7.5" per building code recommendations.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                What is the ideal step rise and run?
              </h3>
              <p className="text-gray-700">
                The ideal step rise (height) is 7-7.5 inches, and the ideal run (depth) is 10-11 inches. The sum of two rises and one run should equal 24-25 inches for comfortable stairs.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How much concrete for 3 steps?
              </h3>
              <p className="text-gray-700">
                For three standard steps (4 feet wide, 11" deep, 7" rise), you'll need approximately 7-8 cubic feet of concrete, which equals about 12-14 bags of 80lb concrete mix.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default ConcreteStepsCalculator
